import { format } from "date-fns";
import { motion } from "framer-motion";
import {
    MessageCircle,
    CheckCircle2,
    HelpCircle,
    Calendar,
    Clock,
} from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { QA } from "./types";

interface QACardProps {
    qa: QA;
    currentStudentId?: string;
    onEdit?: (qa: QA) => void;
}

const getQueryTypeLabel = (type: number) => {
    switch (type) {
        case 1: return "Instruction";
        case 2: return "Tech Support";
        case 3: return "School Admin";
        default: return "Instruction";
    }
};

const getQueryTypeVariant = (type: number) => {
    switch (type) {
        case 1: return "default";
        case 2: return "secondary";
        case 3: return "outline";
        default: return "default";
    }
};

const safelyFormatDate = (dateStr: string | undefined | null, formatStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Invalid Date";
    return format(date, formatStr);
};

export const QACard = ({ qa, currentStudentId, onEdit }: QACardProps) => {
    const isOwner = currentStudentId === qa.studentId;
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-lg shadow-sm p-6 mb-4 hover:shadow-md transition-all border border-gray-100"
        >
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-start space-x-3 flex-1">
                    <motion.div
                        className="mt-1"
                        whileHover={{ rotate: 15 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="bg-primary/10 p-2 rounded-full">
                            <HelpCircle className="h-5 w-5 text-primary" />
                        </div>
                    </motion.div>
                    <div className="flex-1">
                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-lg flex items-center">
                                Question
                            </h3>
                            <Badge variant={getQueryTypeVariant(qa.queryType)}>
                                {getQueryTypeLabel(qa.queryType)}
                            </Badge>
                            {isOwner && !qa.isAnswered && onEdit && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onEdit(qa)}
                                    className="ml-2 h-7 px-2"
                                >
                                    <Clock className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-2">
                            Asked by: {qa.studentName}
                        </div>
                        <p className="text-gray-700 mt-1 text-base leading-relaxed">
                            {qa.queryText}
                        </p>

                        <div className="flex items-center text-sm text-gray-500 mt-2">
                            <Calendar className="h-4 w-4 mr-1" />
                            {safelyFormatDate(qa.createdOn, "MMM d, yyyy")}
                        </div>
                    </div>
                </div>
            </div>

            {qa.reply && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mt-4 pt-4 border-t border-dashed"
                >
                    <div className="flex items-start space-x-3">
                        <motion.div
                            className="mt-1"
                            whileHover={{ rotate: -15 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="bg-green-50 p-2 rounded-full">
                                <MessageCircle className="h-5 w-5 text-green-500" />
                            </div>
                        </motion.div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-base mb-2 flex items-center">
                                Answer
                                <motion.div
                                    whileHover={{ scale: 1.2 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <CheckCircle2 className="h-4 w-4 ml-2 text-green-500" />
                                </motion.div>
                            </h4>
                            <p className="text-gray-700 text-base leading-relaxed">
                                {qa.reply.answerText}
                            </p>
                            <div className="flex items-center text-sm text-gray-500 mt-2">
                                Answered by: {qa.reply.replierName}
                            </div>
                            <div className="mt-2 text-sm text-gray-500 flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                Answered on {safelyFormatDate(qa.updatedOn || qa.updatedon, "MMM d, yyyy")}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {!qa.reply && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mt-4 pt-4 border-t border-dashed"
                >
                    <div className="bg-yellow-50 rounded-lg p-4">
                        <p className="text-yellow-600 text-sm flex items-center">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            >
                                <Clock className="h-4 w-4 mr-2" />
                            </motion.div>
                            Waiting for answer... We'll notify you when your question is
                            answered! 🔔
                        </p>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};
